import axios from 'axios';
import { userCookie } from '~/models/UserCookie';
import * as Sentry from '@sentry/react';

export const URLs = {
  api: '/api',
  /**
   * Nextjs/express endpoint that passes requests through to the monoliht
   */
  monoproxy: '/api/monoproxy',
  login: `/oauth/token`,
  user: `/api/user`,
  sona: '/sona',
  tako: '/tako',
  makomo: '/makomo',
  logout: `/api/user/logout`,
};

const corsUrlStr = process.env.NEXT_PUBLIC_CORS_API || '';
const corsUrlArr = corsUrlStr.split(';');

const TIME_OUT = process.env.NODE_ENV !== 'production' ? 60 * 1000 : 15000; // 15 sec

// redux store
let reduxStore = null;

const timezoneAbbr = new Date().toLocaleString('en', { timeZoneName: 'short' }).split(' ').pop();

const timezoneMap = {
  PST: 'Pacific Time (US & Canada)',
  PDT: 'Pacific Time (US & Canada)',
  EST: 'Eastern Time (US & Canada)',
  EDT: 'Eastern Time (US & Canada)',
  CDT: 'Central Time (US & Canada)',
  CST: 'Central Time (US & Canada)',
};

const defaultHeaders = {
  'client-id': process.env.CLIENT_ID,
  'time-zone': timezoneMap[timezoneAbbr] || timezoneMap.CDT,
  'Content-Type': 'application/json',
  // Default service region id, stupid
  'api-service-region-id': 1,
};

const getHeaders = () => {
  const headers = {
    'chowbus-client-version': process.env.APP_VERSION_MONOTONIC,
  };
  if (userCookie.accessToken && userCookie.tokenType) {
    headers.Authorization = userCookie.tokenType + ' ' + userCookie.accessToken;
  }
  if (userCookie.serviceRegionId) {
    headers['api-service-region-id'] = userCookie.serviceRegionId;
  }
  return { ...defaultHeaders, ...headers };
};

const instance = axios.create({ timeout: TIME_OUT, headers: defaultHeaders });

export const setStore = (store) => {
  reduxStore = store;
};

class ApiError extends Error {
  constructor(error, url) {
    super(error);
    this.name = 'ApiError';
    if (error?.response?.data?.data) {
      this.data = error.response.data.data;
    }
    if (error?.response?.data?.errors) {
      this.errors = error.response.data.errors;
    } else if (error?.response?.data?.error_description) {
      this.errors = error.response.data.error_description;
    }
    this.url = url;
  }

  toString() {
    const msg = `${this.message} - ${this.url}`;
    if (this.errors) {
      return `${msg} :: ${this.errors.join('. ')}`;
    }
    return msg;
  }
}

export const enableErrorHandler = () => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const apiError = new ApiError(error, error?.config?.url);
      if (error.response && error.response.status === 401) {
        const { logout } = require('../store/legacy/actions/userActions.js');
        reduxStore.dispatch(logout());
      } else {
        Sentry.addBreadcrumb({
          category: 'request',
          message: error.message,
          level: Sentry.Severity.Error,
          data: { apiError },
        });
        Sentry.captureException(apiError);
      }
      throw apiError;
    }
  );
};

/**
 * Make a GET call to a service
 * @param {Object} [params]
 * @param {String} params.url
 * @param {Object} [params.data]
 * @param {Object} [params.options]
 */
export const get = ({ url, data, options = {} }) => {
  const headers = getHeaders();
  const request = {
    url,
    method: 'GET',
    headers,
    params: data,
    baseURL: getBaseUrl(options, url),
    timeout: TIME_OUT,
    errorHandle: false,
  };
  return instance(request);
};

/**
 *
 * @param {object} props
 * @param {string} props.url
 * @param {any} [props.data]
 * @param {object} [props.options]
 * @param {string} [props.options.API_BASE] defaults to the monolith server for the current environment,
 *  but if you pass `thisServer` there will be no server prepended to your route. You can pair this with the
 *  URLs.monoproxy route to pass your request through the proxy to the monolith
 */
export const post = ({ url, data, options = {} }) => {
  const headers = getHeaders();
  const request = {
    method: 'POST',
    url,
    data: JSON.stringify(data),
    headers,
    baseURL: getBaseUrl(options, url),
    timeout: TIME_OUT,
  };

  return instance(request);
};

export const put = ({ url, data, options = {} }) => {
  const headers = getHeaders();
  const request = {
    method: 'PUT',
    url,
    data: JSON.stringify(data),
    headers,
    baseURL: getBaseUrl(options, url),
    timeout: TIME_OUT,
  };

  return instance(request);
};

export const del = ({ url, options = {} }) => {
  const headers = getHeaders();
  const request = {
    method: 'DELETE',
    url,
    headers,
    baseURL: getBaseUrl(options, url),
    timeout: TIME_OUT,
  };
  if (options.data) {
    request.data = options.data;
  }

  return instance(request);
};

function getBaseUrl(options, url) {
  // only in local_dev and NEXT_PUBLIC_LOCAL_ENV configuration, fix cors url
  if (
    process.env.NEXT_PUBLIC_LOCAL_ENV === 'localDev' &&
    corsUrlArr.length &&
    (options.CORS_API || corsUrlArr.includes(url))
  ) {
    return '/dev_api';
  }

  let baseURL;
  if (options.API_BASE === 'thisServer') {
    baseURL = '';
  } else if (options.API_BASE) {
    baseURL = options.API_BASE;
  } else {
    baseURL = process.env.API_BASE;
  }
  return baseURL;
}

export function defaultSWRGetter(url) {
  return get({
    url,
    options: {
      API_BASE: 'thisServer',
    },
  });
}
